/**
 * Identity management — `docs/SCREENS.md` §3.
 *
 * THE DEFAULT TAB IS "ALL", and the rows are ordered by name rather than by kind. Both are rules,
 * not preferences. A directory that defaults to People puts agents behind a click, and a list
 * ordered by kind puts every agent below every human so the reader stops scrolling — either one
 * rebuilds, in the interface, exactly the separation the data model was designed to prevent.
 *
 * Every row carries a KindBadge. Not decoration: without it a reviewer scans a directory and
 * assumes they are looking at people, which is the assumption the whole product exists to break.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { IdentityKind } from "@hub/core/browser";
import { useApi } from "../lib/api";
import { Card, KindBadge, KpiCard, ParityStrip, Pill, TierBadge } from "../components";

interface Row {
  id: string;
  kind: IdentityKind;
  displayName: string;
  status: string;
  lifecycleState: string;
  sourceSystem: string;
  lastSeenAt: string | null;
  owner: { id: string; displayName: string } | null;
  agentProfile: { capabilityTier: 1 | 2 | 3; owningTeam: string; dataScopes: string[] } | null;
}

type Tab = "all" | IdentityKind;

export function Identities() {
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const { data, error, loading } = useApi<{ identities: Row[] }>("/v1/identities?limit=500");

  const rows = data?.identities ?? [];
  const humans = rows.filter((r) => r.kind === "human");
  const agents = rows.filter((r) => r.kind === "agent");

  const shown = useMemo(() => rows
    .filter((r) => tab === "all" || r.kind === tab)
    .filter((r) => q === "" || r.displayName.toLowerCase().includes(q.toLowerCase())),
    [rows, tab, q]);

  const withOwner = agents.filter((a) => a.owner !== null).length;
  const stale = rows.filter((r) => r.lastSeenAt === null).length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Identity management</h1>
          <p className="sub">
            One directory. A person and an agent are the same kind of record, differing in the
            mechanics of how they authenticate and how long their credentials live.
          </p>
        </div>
        <Link className="btn" to="/onboard">Add identity</Link>
      </div>

      <ParityStrip
        human={{ n: humans.length.toLocaleString(), label: "people" }}
        agent={{ n: agents.length.toLocaleString(), label: "agents" }}
        principle="Same lifecycle · same review · same policy engine"
      />

      <div className="grid k4" style={{ marginBottom: 14 }}>
        <KpiCard
          label="Strong authentication"
          value={rows.length === 0 ? "—" : "100%"}
          dual={{ human: "SSO + MFA", agent: "workload identity" }}
          delta="People federate; agents present a workload identity. Neither holds a password."
        />
        <KpiCard
          label="Accountable ownership"
          value={agents.length === 0 ? "—" : `${Math.round((withOwner / agents.length) * 100)}%`}
          dual={{ human: "n/a", agent: `${withOwner} of ${agents.length}` }}
          delta="A person need not be owned. An agent must be — enforced by a database constraint."
        />
        <KpiCard
          label="Lifecycle events (30d)"
          value={rows.filter((r) => r.lifecycleState !== "active").length}
          dual={{
            human: humans.filter((r) => r.lifecycleState !== "active").length,
            agent: agents.filter((r) => r.lifecycleState !== "active").length,
          }}
        />
        <KpiCard
          label="Never seen acting"
          value={stale}
          dual={{
            human: humans.filter((r) => r.lastSeenAt === null).length,
            agent: agents.filter((r) => r.lastSeenAt === null).length,
          }}
          delta="An identity that has never acted is either new or should not exist."
        />
      </div>

      <Card title="Directory">
        <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div className="chips">
            {(["all", "human", "agent"] as Tab[]).map((t) => (
              <button key={t} className={`chip ${tab === t ? "on" : ""}`} onClick={() => setTab(t)}
                      data-testid={`tab-${t}`}>
                {t === "all" ? "All" : t === "human" ? "People" : "Agents"}
              </button>
            ))}
          </div>
          <input
            className="btn"
            style={{ flex: 1, minWidth: 180, cursor: "text" }}
            placeholder="Search by name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search the directory"
          />
        </div>

        {error && <p className="err">{error.message}{error.hint ? ` — ${error.hint}` : ""}</p>}
        {loading && <p className="empty">Loading…</p>}
        {!loading && !error && shown.length === 0 && (
          <p className="empty">No identities match. That is a filter result, not an empty estate.</p>
        )}

        {shown.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Identity</th>
                  <th>Kind</th>
                  <th className="hide-m">Source</th>
                  <th className="hide-m">Owner</th>
                  <th>Lifecycle</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className="clickable" data-testid="identity-row">
                    <td>
                      <Link to={r.kind === "agent" ? `/agents/${r.id}` : `/identities/${r.id}`}>
                        {r.displayName}
                      </Link>
                      {r.agentProfile && (
                        <> <TierBadge tier={r.agentProfile.capabilityTier} /></>
                      )}
                    </td>
                    {/* Every row, both tabs. */}
                    <td><KindBadge kind={r.kind} /></td>
                    <td className="hide-m" style={{ color: "var(--muted)" }}>{r.sourceSystem}</td>
                    <td className="hide-m" style={{ color: "var(--muted)" }}>
                      {r.owner?.displayName ?? (r.kind === "agent" ? "—" : "not owned")}
                    </td>
                    <td>
                      <Pill tone={
                        r.lifecycleState === "leaver" || r.lifecycleState === "retired" ? "bad"
                          : r.lifecycleState === "active" ? "ok" : "warn"
                      }>
                        {r.lifecycleState}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
